import { serializeJsonLd, toSchemaDate } from '../../shared/utils/seo';
import React from 'react';

import { Col, Row } from 'antd';
import { getDate } from '../../shared/utils/date';
import { News } from '../../shared/models/news';
import { getNewsById } from '../../lib/firestoreAdmin';
import BaseHead from '../../components/Base/BaseHead';
import Description from '../../components/Description';
import Head from 'next/head';
import { convertTitleToUrl } from '../../shared/utils/url';
import { storageImage } from '../../shared/utils/storage-image';

export async function getServerSideProps({ query, res }: { query: any; res: any }) {
  const newsId = query?.id?.split('__')[1];

  const news = await getNewsById(newsId);

  if (!news) {
    return { notFound: true };
  }

  // 301 redirect if slug is stale
  const canonicalSlug = convertTitleToUrl('haber', news.haber_baslik, news.id).replace(/^\/haber\//, '');
  if (`${query?.id}` !== canonicalSlug) {
    return { redirect: { destination: `/haber/${canonicalSlug}`, permanent: true } };
  }

  res.setHeader('Cache-Control', 'public, s-maxage=3600, stale-while-revalidate=86400');

  return {
    props: { news },
  };
}

function HaberDetay({ news }: { news: News.News }) {
  const pubDate = toSchemaDate(news.haber_tarih);
  const modifiedDate = toSchemaDate((news as any).updatedAt) || pubDate;
  const newsPageUrl = `https://tenis.io${convertTitleToUrl('haber', news.haber_baslik, news.id)}`;
  const articleSchema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'NewsArticle',
        headline: news?.haber_baslik,
        url: newsPageUrl,
        mainEntityOfPage: newsPageUrl,
        image: news?.haber_foto_url ? [news.haber_foto_url] : ['https://tenis.io/images/og-image.jpg'],
        ...(pubDate && { datePublished: pubDate }),
        ...(modifiedDate && { dateModified: modifiedDate }),
        author: { '@type': 'Organization', name: 'tenis.io', url: 'https://tenis.io' },
        publisher: {
          '@type': 'Organization',
          name: 'tenis.io',
          logo: { '@type': 'ImageObject', url: 'https://tenis.io/images/logo.png' },
        },
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Ana Sayfa', item: 'https://tenis.io' },
          { '@type': 'ListItem', position: 2, name: 'Tenis Haberleri', item: 'https://tenis.io/haberler' },
          { '@type': 'ListItem', position: 3, name: news.haber_baslik, item: newsPageUrl },
        ],
      },
    ],
  };

  const description = news?.haber_detay
    ? `${news.haber_detay.replace(/<[^>]*>/g, '').slice(0, 155)}...`
    : `${news?.haber_baslik} - tenis.io haberleri`;

  return (
    <>
      <BaseHead
        type="article"
        pageTitle={news.seo_baslik || news.haber_baslik}
        description={news.seo_aciklama || description}
        imageUrl={news?.haber_foto_url}
        noindex={!news.aktif_mi || news.seo_indeksle === false || Boolean(news.external_url)}
      />
      <Head>
        {pubDate && <meta property="article:published_time" content={pubDate} />}
        {modifiedDate && <meta property="article:modified_time" content={modifiedDate} />}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: serializeJsonLd(articleSchema) }}
        />
      </Head>

      <div className="container mt-xl mb-xl">
        <Row gutter={[24, 24]}>
          <Col xs={24}>
            <img
              className="news-image-full"
              src={storageImage(news.haber_foto_url, 640) || '/images/og-image.jpg'}
              alt={news.haber_baslik}
              width={1200}
              height={630}
              loading="eager"
              onError={event => {
                const image = event.currentTarget;
                if (!image.src.endsWith('/images/og-image.jpg')) {
                  image.src = '/images/og-image.jpg';
                  image.alt = 'tenis.io';
                }
              }}
              {...({ fetchpriority: 'high' } as any)}
            />
          </Col>

          <Col xs={24}>
            <h1>{news?.haber_baslik}</h1>
            <p>
              <time dateTime={pubDate}>{getDate(news?.haber_tarih)}</time>
            </p>
          </Col>

          <Col xs={24}>
            <Description description={news?.haber_detay} descriptionHtml={news?.haber_detay_html} collapsible={false} />
          </Col>
        </Row>
      </div>
    </>
  );
}

export default HaberDetay;
